/**
 * Утилита для работы с параметрами templates из URL
 * Параметры сохраняются в sessionStorage и очищаются после перехода в канвас
 */

export interface TemplatesParams {
  from: string;
  style?: string;
  color?: string;
  font?: string;
  theme_id?: string;
  shape?: string;
}

const STORAGE_KEY = 'templates_params';

/**
 * Сохраняет параметры templates из URL в sessionStorage
 * Сохраняет если есть from=templates (theme_id не обязателен)
 */
export const saveTemplatesParams = (searchParams: URLSearchParams): void => {
  const from = searchParams.get('from');
  // Проверяем наличие from=templates (theme_id не обязателен)
  if (from === 'templates') {
    const params: TemplatesParams = {
      from,
      style: searchParams.get('style') || undefined,
      color: searchParams.get('color') || undefined,
      font: searchParams.get('font') || undefined,
      theme_id: searchParams.get('theme_id') || undefined,
      shape: searchParams.get('shape') || undefined,
    };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(params));
  }
};

/**
 * Получает сохраненные параметры templates из sessionStorage
 * Если в sessionStorage нет, пытается прочитать из URL (для избежания race condition)
 */
export const getTemplatesParams = (): TemplatesParams | null => {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored) as TemplatesParams;
    }
  } catch {
    // Игнорируем ошибки парсинга
  }

  // Если в sessionStorage нет, проверяем URL напрямую
  if (typeof window !== 'undefined') {
    const urlParams = new URLSearchParams(window.location.search);
    const from = urlParams.get('from');
    // Проверяем наличие from=templates (theme_id не обязателен)
    if (from === 'templates') {
      const params: TemplatesParams = {
        from,
        style: urlParams.get('style') || undefined,
        color: urlParams.get('color') || undefined,
        font: urlParams.get('font') || undefined,
        theme_id: urlParams.get('theme_id') || undefined,
        shape: urlParams.get('shape') || undefined,
      };
      // Сохраняем в sessionStorage для будущих проверок
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(params));
      return params;
    }
  }

  return null;
};

/**
 * Проверяет, есть ли параметр from=templates в URL
 * Сначала проверяет URL, затем sessionStorage (для избежания race condition)
 */
export const isFromTemplates = (): boolean => {
  // Сначала проверяем URL напрямую (приоритет URL над sessionStorage)
  if (typeof window !== 'undefined') {
    const urlParams = new URLSearchParams(window.location.search);
    const from = urlParams.get('from');
    // Проверяем наличие from=templates (theme_id не обязателен)
    if (from === 'templates') {
      // Сохраняем параметры из URL в sessionStorage для будущих проверок
      saveTemplatesParams(urlParams);
      return true;
    }
    // Если в URL нет from=templates, но есть в sessionStorage - очищаем sessionStorage
    // Это предотвращает использование устаревших данных
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) {
      clearTemplatesParams();
    }
  }

  // Если в URL нет, проверяем sessionStorage (для избежания race condition при первой загрузке)
  const params = getTemplatesParams();
  if (params && params.from === 'templates') {
    return true;
  }

  return false;
};

/**
 * Очищает сохраненные параметры templates
 */
export const clearTemplatesParams = (): void => {
  sessionStorage.removeItem(STORAGE_KEY);
};

/**
 * Формирует Google OAuth ссылку с учетом templates параметров
 * Если есть theme_id И from=templates одновременно, формирует ссылку с параметром next
 * @param currentSearch - текущие query параметры из URL
 * @returns URL для Google OAuth
 */
export const buildGoogleOAuthUrl = (currentSearch: string): string => {
  const searchParams = new URLSearchParams(currentSearch);
  const themeId = searchParams.get('theme_id');
  const from = searchParams.get('from');

  // Если есть theme_id И from=templates одновременно, формируем ссылку с next параметром
  if (themeId && from === 'templates') {
    // Собираем все параметры templates
    const templatesParams = new URLSearchParams();
    templatesParams.set('from', 'templates');

    const style = searchParams.get('style');
    const color = searchParams.get('color');
    const font = searchParams.get('font');
    const theme_id = searchParams.get('theme_id');
    const shape = searchParams.get('shape');
    const template = searchParams.get('template');

    if (style) templatesParams.set('style', style);
    if (color) templatesParams.set('color', color);
    if (font) templatesParams.set('font', font);
    if (theme_id) templatesParams.set('theme_id', theme_id);
    if (shape) templatesParams.set('shape', shape);
    if (template) templatesParams.set('template', template);

    // Формируем next параметр с полным URL на text-to-slides
    const nextUrl = `/workspace/text-to-slides/?${templatesParams.toString()}`;

    // Формируем финальную ссылку с next параметром
    const oauthParams = new URLSearchParams();
    oauthParams.set('next', nextUrl);

    return `/social/login/google-oauth2/?${oauthParams.toString()}`;
  }

  // Обычная логика: просто добавляем текущие параметры
  return `/social/login/google-oauth2/${currentSearch ? currentSearch : ''}`;
};
